import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelToDisplay, getObjectHash, getZombieInstanceFromPrimaryKey } from '@iotv/datamodel-core';
import { ExpressionFunctionType, } from '@iotv/iotv-v3-types';
import SendIcon from '@mui/icons-material/Send';
import { Button, Checkbox, Grid, Stack, ThemeProvider, ToggleButton, ToggleButtonGroup, Toolbar, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { iterateQueryWithSource, queryByGraphFormatter } from '../../../data/Wideawake/WideawakeWrapper';
import { queryByGraph } from '../../../data/Wideawake/wideawakeDataFns';
import { useDocumentClientQuery } from '../../../hooks/useDocumentClientQueryV2';
import { getDevicesViaCustomer, getDevicesViaProperty } from '../../templates/slot3/devTsxTabs/WideawakeTabs/queryPaths';
import { StripedDataGrid } from '../DataGrid/styles/striped';
import { formatParameters } from './QueryParamFormatter';
import { condensedButtons } from '../../../cosmetics/Themes/MuiInputSmaller';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Renders a modal selection component with left accordion being populated with series of parameter selection components, the right being populated with a reults table
 * The definition for the results table is injected and accepts getter setter state functions to allow selection of result items to be set in the parent component
 * @param param0
 * @returns
 */
export function ContextQueryModal(props) {
    const { componentParamPairsMap, parentTableUseSelectedObjects, getDataGridProps, lastSearchedItemsRef, lastQueryParamsRef } = props;
    const { state: [[selectedObjects, setSelectedObjects]], handlers: { addToSelectedObjects, removeFromSelectedObjects, isInSelectedObjects, getFromSelectedObjects, togglePresenceInSelectedObjects }, } = parentTableUseSelectedObjects;
    const [params, setParams] = useState(Array(Object.keys(componentParamPairsMap).length).fill(undefined));
    const [viewingOptions, setViewingOptions] = useState(['EUIHas']);
    const [filterType, setFilterType] = useState('AND');
    // cannot filter via customer & property at the same time, as they can cause no overlap on an AND condition
    const restrictionList = {
        Customer: ['Property'],
        Property: ['Customer'],
    };
    useEffect(() => {
        if (params.length === Object.keys(componentParamPairsMap).length) {
            const paramCopy = [...params];
            setParams(paramCopy);
        }
    }, []);
    const [paramsCombined, setParamsCombined] = useState(undefined);
    const paramsHash = getObjectHash(paramsCombined);
    const hookParams = {
        query: () => convertPreset(),
        queryParams: paramsCombined,
        dependencies: [paramsHash],
    };
    const convertPreset = async () => {
        const spreaded = params.flat().filter((val) => val !== undefined);
        const [modifiedParams, source] = formatParameters(spreaded);
        const res = [];
        const filterByAnd = filterType === 'AND';
        if (filterByAnd) {
            modifiedParams.push({ key: 'pk', value: 'Device', predicate: ExpressionFunctionType.BEGINS_WITH });
        }
        debug && console.log('modifiedParams', modifiedParams);
        debug && console.log('source', source);
        if (source) {
            const sourceVBO = getZombieInstanceFromPrimaryKey(source);
            let queryFormat;
            if (sourceVBO.type === 'Property') {
                queryFormat = getDevicesViaProperty([sourceVBO], modifiedParams);
            }
            else if (sourceVBO.type === 'Customer') {
                queryFormat = getDevicesViaCustomer([sourceVBO], modifiedParams);
            }
            else {
                debug && console.log('Failed');
                throw Error('Source VBO for device query is not valid failed:' + sourceVBO.type);
            }
            const queryRes = await queryByGraph(queryFormat);
            const formatted = queryByGraphFormatter(queryRes);
            res.push(...formatted);
        }
        if (filterByAnd === false || source === undefined) {
            const found = await iterateQueryWithSource('Device', filterByAnd, modifiedParams);
            res.push(...found);
        }
        const convert = { err: null, data: { Items: res } };
        return convert;
    };
    const updateParams = (passed, index) => {
        const paramsCopy = [...params];
        paramsCopy[index] = passed;
        const restricedKeys = Object.keys(restrictionList);
        if (passed) {
            const passedKeys = passed.map((val) => val.key);
            passedKeys.forEach((passedKey) => {
                if (restricedKeys.includes(passedKey)) {
                    const illegalKeys = restrictionList[passedKey];
                    illegalKeys.forEach((val) => {
                        const index = paramsCopy.findIndex((param) => param !== undefined && param.map((inEachKey) => inEachKey.key).includes(val));
                        if (index !== -1) {
                            paramsCopy[index] = undefined;
                        }
                    });
                }
            });
        }
        setParams(paramsCopy);
    };
    const constructQueryFilterState = () => {
        if (params) {
            if (params.length === 0) {
                return;
            }
            const spreaded = params.flat().filter((val) => val !== undefined);
            if (filterType === 'AND') {
                spreaded.push({ key: 'pk', value: 'Device', predicate: ExpressionFunctionType.BEGINS_WITH });
            }
            setParamsCombined([...spreaded]);
        }
    };
    const handlePanelChange = (key) => {
        const keys = Object.keys(restrictionList);
        let filterList = [];
        let newViewingOptions = [...viewingOptions];
        if (keys.includes(key)) {
            const filterOut = restrictionList[key];
            filterList = filterOut;
            newViewingOptions = newViewingOptions.filter((item) => !filterList.includes(item));
        }
        if (newViewingOptions.includes(key)) {
            setViewingOptions(newViewingOptions.filter((item) => item !== key));
        }
        else {
            setViewingOptions([...newViewingOptions, key]);
        }
    };
    const { querySetterGetters: [[items, setItems]], inFlight, } = useDocumentClientQuery(hookParams);
    useEffect(() => {
        setItems(selectedObjects);
    }, []);
    const itemsHash = items.map((item) => item.sk).join(',');
    useEffect(() => {
        lastSearchedItemsRef.current = items;
    }, [itemsHash]);
    const dataGridProps = getDataGridProps({
        items: items,
        loading: inFlight,
        addToSelectedObjects,
        isInSelectedObjects,
        removeFromSelectedObjects,
        selectedObjects,
        setSelectedObjects,
        getFromSelectedObjects,
        togglePresenceInSelectedObjects,
    });
    const keyPress = (e) => {
        if (e.key === 'Enter') {
            if (params?.length !== 0) {
                constructQueryFilterState();
            }
        }
    };
    const filterChange = (e) => {
        setFilterType(e.target.value);
    };
    return (_jsx("div", { style: { minHeight: '60vh' }, children: _jsxs(Grid, { container: true, columnSpacing: 1.5, children: [_jsxs(Grid, { container: true, size: 4, children: [_jsx(ThemeProvider, { theme: condensedButtons, children: _jsx("div", { style: { overflow: 'auto', maxHeight: '65vh', scrollbarGutter: 'stable', scrollbarWidth: 'thin' }, children: Object.entries(componentParamPairsMap).map(([name, pair], index) => {
                                    const { ParamSelector, ParamSelectorProps } = pair;
                                    // console.log('lastQueryParamsRef', lastQueryParamsRef);
                                    const lastQuery = undefined;
                                    const selected = viewingOptions.includes(name);
                                    // let lastQuery: MapLike<any> | undefined = k === openQueryKey ? lastQueryParamsRef.current : undefined;
                                    return (_jsx(ParamSelector, { updateParams,
                                        selected,
                                        keyPress,
                                        lastQuery,
                                        lastQueryParamsRef,
                                        index,
                                        clearSelectedObjects: () => setSelectedObjects([]),
                                        ...ParamSelectorProps, children: _jsxs(Toolbar, { variant: "dense", style: { maxHeight: '15px', padding: 0 }, children: [_jsx(Checkbox, { checked: selected, onClick: () => handlePanelChange(name) }), _jsx(Typography, { fontSize: '12px', sx: { width: '80px', flexShrink: 0 }, children: camelToDisplay(name) })] }) }, name));
                                }) }) }), _jsx(Grid, { size: 12, children: _jsxs(Stack, { direction: "row", spacing: 1, sx: {
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                }, children: [_jsx(Tooltip, { title: `Retrieved result must match ${filterType === 'AND' ? ' ALL' : 'at least ONE'} of the filters set.`, children: _jsxs(ToggleButtonGroup, { color: "primary", style: { marginLeft: 10, marginRight: 10 }, size: "small", value: filterType, exclusive: true, onChange: filterChange, children: [_jsx(ToggleButton, { value: "AND", children: "AND" }), _jsx(ToggleButton, { value: "OR", children: "OR " })] }) }), _jsx(Button, { fullWidth: true, style: { marginLeft: 20, paddingTop: 5, paddingBottom: 5 }, onClick: constructQueryFilterState,
                                        disabled: params?.length === 0,
                                        variant: 'contained',
                                        children: 'Search',
                                        endIcon: _jsx(SendIcon, {}) })] }) })] }), _jsx(Grid, { size: 8, children: dataGridProps && _jsx(StripedDataGrid, { ...dataGridProps }) })] }) }));
}
